import {
  useCreateLessonPlanMutation,
  useDeleteLessonPlanMutation,
} from '@/app/redux/endpoints/lessonPlans';
import { useAppDispatch, useAppSelector } from '@/app/redux/hooks';
import { useLessonPlans } from '@/app/redux/hooks/lessonPlans';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import {
  openConfirmationDialogue,
  setConfirmationDialogueCallback,
} from '@/app/redux/slices/modalsSlice';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import { LessonPlan } from '@/common/types/lessonPlan';
import {
  DashboardViewActions,
  DashboardViewContainer,
  DashboardViewContent,
  DashboardViewHeader,
  DashboardViewTitle,
} from '@/common/ui/DashboardViewStyles';
import SearchInput from '@/common/ui/SearchInput';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import {
  getFromLocalStorage,
  saveToLocalStorage,
} from '@/common/utils/localStorage';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import {
  ChalkboardTeacherIcon,
  ListIcon,
  PlusIcon,
  SquaresFourIcon,
} from '@phosphor-icons/react';
import { Badge } from '@shadcn/components/badge';
import { Button } from '@shadcn/components/button';
import { Skeleton } from '@shadcn/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@shadcn/components/table';
import { ToggleGroup, ToggleGroupItem } from '@shadcn/components/toggle-group';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import LessonPlanCard from './LessonPlanCard';
import LessonPlansTableRow from './LessonPlansTableRow';

type ViewMode = 'gallery' | 'list';

export default function LessonPlansView() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { currentOrganisationId } = useCurrentOrganisationId();
  const userId = useAppSelector(selectUserId);

  const { lessonPlans, isLoading } = useLessonPlans();
  const [deleteLessonPlan] = useDeleteLessonPlanMutation();
  const [createLessonPlan] = useCreateLessonPlanMutation();

  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>(
    getFromLocalStorage('lessonplans-view-mode') || 'gallery',
  );

  useEffect(() => {
    saveToLocalStorage('lessonplans-view-mode', viewMode);
  }, [viewMode]);

  const filteredLessonPlans = useMemo(() => {
    if (!searchTerm) return lessonPlans;
    return lessonPlans.filter((lp: LessonPlan) =>
      lp.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [searchTerm, lessonPlans]);

  function handleCreateLessonPlan() {
    navigate('/dashboard/lessonplans/new');
  }

  async function handleConfirmDeleteLessonPlan(lessonPlan: LessonPlan) {
    setConfirmationDialogueCallback(async () => {
      await handleDeleteLessonPlan(lessonPlan);
    });
    dispatch(
      openConfirmationDialogue({
        title: 'Delete Lesson Plan',
        description: `Are you sure you want to delete "${lessonPlan.name}"?`,
        variant: 'destructive',
      }),
    );
  }

  async function handleDeleteLessonPlan(lessonPlan: LessonPlan) {
    await deleteLessonPlan({
      orgId: currentOrganisationId!,
      id: lessonPlan.id,
    }).unwrap();
    trackMixpanelEvent('Lesson Plan Deleted', {
      lessonPlanId: lessonPlan.id,
      lessonPlanName: lessonPlan.name,
    });
    toast.success('Lesson plan deleted');
  }

  async function handleCopyLessonPlan(lp: LessonPlan) {
    try {
      const { id: _, createdAt: __, updatedAt: ___, ...rest } = lp;
      const newPlan = await createLessonPlan({
        ...rest,
        createdBy: userId ?? rest.createdBy,
        name: `Copy of ${lp.name}`,
      }).unwrap();
      trackMixpanelEvent('Lesson Plan Copied', {
        lessonPlanId: newPlan.id,
        lessonPlanName: newPlan.name,
        sourceLessonPlanId: lp.id,
        sourceLessonPlanName: lp.name,
      });
      toast.success('Lesson plan copied');
    } catch (e) {
      console.error('Error copying lesson plan:', e);
      toast.error('Failed to copy lesson plan', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    }
  }

  function GallerySkeleton() {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="bg-white rounded-lg border border-gray-200 overflow-hidden"
          >
            <Skeleton className="h-32 w-full" />
            <div className="p-3">
              <Skeleton className="h-4 w-3/4 mb-2" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  function ListSkeleton() {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Updated</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...Array(6)].map((_, i) => (
            <TableRow key={i}>
              <TableCell>
                <Skeleton className="h-5 w-40" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-5 w-20" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-5 w-8" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <DashboardViewContainer>
      <DashboardViewHeader>
        <DashboardViewTitle>
          Lesson Plans
          <Badge className="px-1 text-xxs" variant="secondary">
            {lessonPlans.length}
          </Badge>
        </DashboardViewTitle>
        <DashboardViewActions>
          <ToggleGroup
            type="single"
            value={viewMode}
            onValueChange={(value: ViewMode) => value && setViewMode(value)}
            className="border rounded-md"
          >
            <ToggleGroupItem
              value="gallery"
              aria-label="Gallery view"
              className="px-2"
            >
              <SquaresFourIcon size={18} />
            </ToggleGroupItem>
            <ToggleGroupItem
              value="list"
              aria-label="List view"
              className="px-2"
            >
              <ListIcon size={18} />
            </ToggleGroupItem>
          </ToggleGroup>
          <SearchInput
            placeholder="Search lesson plans..."
            value={searchTerm}
            onChange={setSearchTerm}
          />
          <Button
            variant="outline"
            className="flex gap-2"
            onClick={handleCreateLessonPlan}
          >
            <PlusIcon size={20} />
            Create Lesson Plan
          </Button>
        </DashboardViewActions>
      </DashboardViewHeader>

      <DashboardViewContent>
        {isLoading ? (
          viewMode === 'gallery' ? (
            <GallerySkeleton />
          ) : (
            <ListSkeleton />
          )
        ) : filteredLessonPlans.length > 0 ? (
          viewMode === 'gallery' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4">
              {filteredLessonPlans.map((lp) => (
                <LessonPlanCard
                  key={lp.id}
                  lessonPlan={lp}
                  onCopy={() => handleCopyLessonPlan(lp)}
                  onConfirmDelete={() => handleConfirmDeleteLessonPlan(lp)}
                />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLessonPlans.map((lp) => (
                  <LessonPlansTableRow
                    key={lp.id}
                    lessonPlan={lp}
                    onCopy={() => handleCopyLessonPlan(lp)}
                    onConfirmDelete={() => handleConfirmDeleteLessonPlan(lp)}
                  />
                ))}
              </TableBody>
            </Table>
          )
        ) : (
          <div className="w-full min-h-[70vh] flex flex-col items-center justify-center py-12 gap-6">
            <ChalkboardTeacherIcon
              size={80}
              className="text-neutral-400"
              weight="thin"
            />
            <div className="flex flex-col items-center gap-2">
              <h3 className="text-lg font-medium text-neutral-900">
                {searchTerm
                  ? 'No matching lesson plans'
                  : 'No lesson plans yet'}
              </h3>
              <p className="text-sm text-neutral-500 max-w-[400px] text-center">
                {searchTerm
                  ? 'Try a different search term or create a new lesson plan.'
                  : 'Create your first lesson plan to get started.'}
              </p>
            </div>
            <Button onClick={handleCreateLessonPlan}>
              <PlusIcon size={20} className="mr-2" />
              Create Lesson Plan
            </Button>
          </div>
        )}
      </DashboardViewContent>
    </DashboardViewContainer>
  );
}
